import { Fragment, memo, type ReactNode, useCallback } from 'react';

import type { LearningResource } from '@didaskill/domain/learning-resources/types';
import type { Upload } from '@didaskill/domain/uploads/types';
import { removeButtonAppearance } from '@didaskill/ui/design-system';
import { useBoolState } from '@didaskill/ui/utils';

import { LearningResourceCard } from '../card/LearningResourceCard';
import { LearningResourceDialog } from '../dialog/LearningResourceDialog';

export type LearningResourceItemProps = {
  resource: LearningResource;
  isDownloading?: boolean;
  onDownloadFile?: (file: Upload) => void;
  onPlayVideo?: () => Promise<void>;
  canUpdateLearningResource?: boolean;
  onClickUpdateLearningResource?: (learningResourceId: string) => void;
  canDeleteLearningResource?: boolean;
  onDeleteLearningResource?: (learningResourceId: string) => Promise<void>;
  dialogFooter?: ReactNode;
  defaultOpen?: boolean;
  hideTrigger?: boolean;
  onDialogClose?: () => void;
};

/**
 * Displays a learning resource card and opens its preview dialog on click.
 * @param props.resource Resource shown in the card and dialog.
 * @param props.isDownloading Whether a file download is in progress.
 * @param props.onDownloadFile Called when the student downloads an attached file.
 * @param props.onPlayVideo Called when a video or replay starts playing.
 * @param props.canUpdateLearningResource Whether edit actions are available.
 * @param props.onClickUpdateLearningResource Called when the edit action is triggered.
 * @param props.canDeleteLearningResource Whether delete actions are available.
 * @param props.onDeleteLearningResource Called when the delete action is confirmed.
 * @param props.dialogFooter Optional footer rendered inside the open dialog.
 * @param props.defaultOpen Whether the preview dialog starts open.
 * @param props.hideTrigger Whether to skip the clickable card (dialog-only).
 * @param props.onDialogClose Called after the dialog is closed.
 * @returns The resource card (unless hidden) and its preview dialog.
 */
export const LearningResourceItemBase = ({
  resource,
  isDownloading,
  onDownloadFile,
  onPlayVideo,
  canUpdateLearningResource,
  onClickUpdateLearningResource,
  canDeleteLearningResource,
  onDeleteLearningResource,
  dialogFooter = null,
  defaultOpen = false,
  hideTrigger = false,
  onDialogClose,
}: LearningResourceItemProps): JSX.Element => {
  const [isDialogOpen, onOpenDialog, onCloseDialog] = useBoolState(defaultOpen);

  const handleClickRessourceItem = useCallback(() => {
    onOpenDialog();
  }, [onOpenDialog]);

  const handleKeyDownResourceItem = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      onOpenDialog();
    },
    [onOpenDialog],
  );

  const handleCloseDialog = useCallback(() => {
    onCloseDialog();
    onDialogClose?.();
  }, [onCloseDialog, onDialogClose]);

  return (
    <Fragment>
      {hideTrigger ? null : (
        <div
          role="button"
          tabIndex={0}
          onClick={handleClickRessourceItem}
          onKeyDown={handleKeyDownResourceItem}
          css={removeButtonAppearance}
        >
          <LearningResourceCard
            learningResource={resource}
            canDeleteLearningResource={canDeleteLearningResource}
            canUpdateLearningResource={canUpdateLearningResource}
            onClickUpdateLearningResource={onClickUpdateLearningResource}
            onDeleteLearningResource={onDeleteLearningResource}
          />
        </div>
      )}

      <LearningResourceDialog
        isOpen={isDialogOpen}
        onRequestClose={handleCloseDialog}
        resource={resource}
        isDownloading={isDownloading}
        onDownloadFile={onDownloadFile}
        onPlayVideo={onPlayVideo}
        footer={isDialogOpen ? dialogFooter : null}
      />
    </Fragment>
  );
};

LearningResourceItemBase.displayName = 'LearningResourceItem';

export const LearningResourceItem = memo(LearningResourceItemBase);
